import { useMediaQuery, useTheme } from '@mui/material';
import { mapValues, round } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ExportIcon } from 'assets/icons';
import {
  useAuthStore,
  useMessageSnackbar,
  useSubscription,
} from 'services/hooks';
import { locations } from 'services/routes/locations';
import {
  checkCurrentSongLoading,
  checkProjectHasChanges,
  selectCurrentProjectId,
  selectEffectsState,
} from 'services/store/remixer/selectors';
import { selectStudioExportLimit } from 'services/store/user/selectors';
import { useSaveProject } from 'pages/Remixer/hooks';
import { StudioExportLimitDialog } from 'components/Modals/implementations/StudioExportLimitDialog';
import useAnalytics from 'services/hooks/analytics/useAnalytics';
import { events } from 'services/analytics';
import useExportRemixerSong from '../../../../hooks/useExportRemixerSong';
import { ExportingDialog, RenderingCompleteDialog } from '../ExportingDialog';
import ExportRestrictionDialog from './components/ExportRestrictionDialog';
import { StyledDownloadSongButton } from './styled';

const ExportTrackButton = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { displayErrorSnackbar } = useMessageSnackbar();
  const { track } = useAnalytics();

  const hasChanges = useSelector(checkProjectHasChanges);
  const projectId = useSelector(selectCurrentProjectId);

  const { exportSong, isExporting, song, error } = useExportRemixerSong();

  const { isSubscriptionActive } = useSubscription();
  const { authStore } = useAuthStore();
  const { isLoggedIn } = authStore;
  const isUnpaidAccount = isLoggedIn && !isSubscriptionActive;

  const [isRenderingCompleteDialogOpen, setIsRenderingCompleteDialogOpen] =
    useState(false);
  const [isExportLimitDialogOpen, setIsExportLimitDialogOpen] = useState(false);

  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const effectValues = useSelector(selectEffectsState);
  const { save, isLoading: isSaving } = useSaveProject();
  const isExportModalOpen = isExporting || isSaving;
  const isNewMicroFeatureLoading = useSelector(checkCurrentSongLoading);
  const studioExportLimit = useSelector(selectStudioExportLimit);
  const isStudioExportLimitReached =
    typeof studioExportLimit?.remaining === 'number' &&
    studioExportLimit.remaining < 1;

  const isInitialState = !projectId && !hasChanges;
  const isDisabled = isInitialState || isNewMicroFeatureLoading || isExporting;

  const handleClick = useCallback(async () => {
    const fxContent: typeof effectValues = mapValues(effectValues, (value) =>
      round(value, 2)
    );

    track(events.studioExportClick(fxContent));

    if (!isLoggedIn) {
      navigate(locations.general.register.pathname(''));
      return;
    }

    if (isUnpaidAccount) {
      setShowUpgradeDialog(() => true);
      return;
    }

    if (isStudioExportLimitReached) {
      return setIsExportLimitDialogOpen(true);
    }

    await save();
    await exportSong();
    setIsRenderingCompleteDialogOpen(true);
    track(events.studioExport(fxContent));
  }, [
    effectValues,
    isLoggedIn,
    isUnpaidAccount,
    isStudioExportLimitReached,
    save,
    exportSong,
    track,
    navigate,
  ]);

  const handleUpgradeDialogClose = useCallback(() => {
    setShowUpgradeDialog(false);
  }, []);
  const handleStudioExportLimitDialogClose = () =>
    setIsExportLimitDialogOpen(false);

  const handleRenderingCompleteDialogClose = useCallback(() => {
    navigate(locations.library.tracks.studio.pathname());
    setIsRenderingCompleteDialogOpen(false);
  }, [navigate]);

  useEffect(() => {
    if (!error) {
      return;
    }
    displayErrorSnackbar(t('remixer:snackbarMessages:exportTrackError'));
  }, [error, displayErrorSnackbar, t]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      <StyledDownloadSongButton
        loading={isExporting}
        disabled={isDisabled}
        startIcon={<ExportIcon />}
        onClick={handleClick}
        size="large"
        variant={isMobile ? 'text' : 'rounded'}
      >
        {!isMobile && t('remixer:header:exportTrack')}
      </StyledDownloadSongButton>

      <ExportingDialog isOpen={isExportModalOpen} />
      {song && (
        <RenderingCompleteDialog
          isOpen={isRenderingCompleteDialogOpen}
          onDialogClose={handleRenderingCompleteDialogClose}
        />
      )}

      <StudioExportLimitDialog
        onClose={handleStudioExportLimitDialogClose}
        isOpen={isExportLimitDialogOpen}
      />
      <ExportRestrictionDialog
        isOpen={showUpgradeDialog}
        onClose={handleUpgradeDialogClose}
      />
    </>
  );
};

export default ExportTrackButton;
